import { isValidElement, type ReactNode } from 'react';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';

/**
 * Tint of the icon bubble in the card's top-right corner.
 *   - info     → Atmosphere (default — neutral "measurement" tone)
 *   - positive → Bright Moss (inflows, growth)
 *   - negative → Fresh Salmon (outflows, burn, overdue)
 *   - neutral  → quiet grey
 */
export type MetricTone = 'info' | 'positive' | 'negative' | 'neutral';

const TONE_CLASS: Record<MetricTone, string> = {
  info: 'bg-info-soft/30 text-info',
  positive: 'bg-bright-moss/35 text-bright-moss-dark',
  negative: 'bg-fresh-salmon/25 text-fresh-salmon-dark',
  neutral: 'bg-secondary text-muted-foreground',
};

/**
 * Callers historically coloured the icon directly (`text-fresh-salmon-dark`
 * etc.). Rather than touch every page, read that intent off the icon's
 * className so the bubble tint agrees with it. An explicit `tone` prop
 * always wins.
 */
function inferTone(icon: ReactNode): MetricTone {
  if (!isValidElement<{ className?: string }>(icon)) return 'info';
  const cls = icon.props.className ?? '';
  if (cls.includes('fresh-salmon')) return 'negative';
  if (cls.includes('bright-moss')) return 'positive';
  return 'info';
}

interface MetricCardProps {
  label: string;
  value: string;
  delta?: number | null;
  deltaLabel?: string;
  loading?: boolean;
  hint?: string;
  icon?: ReactNode;
  tone?: MetricTone;
  className?: string;
}

export function MetricCard({
  label,
  value,
  delta,
  deltaLabel,
  loading,
  hint,
  icon,
  tone,
  className,
}: MetricCardProps) {
  const resolvedTone = tone ?? inferTone(icon);
  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        {icon && (
          <span
            className={cn(
              // `[&_svg]:text-current` out-specifies the per-icon colour
              // utility so the glyph inherits the bubble's tone.
              'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg [&_svg]:text-current',
              TONE_CLASS[resolvedTone],
            )}
          >
            {icon}
          </span>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-32" />
        ) : (
          <div className="text-2xl font-medium tabular-nums tracking-headline text-foreground">
            {value}
          </div>
        )}
        {delta != null && !loading && (
          <div className="mt-1 flex items-center gap-1 text-xs">
            {delta > 0 ? (
              <ArrowUpRight className="h-3 w-3 text-bright-moss-dark" />
            ) : delta < 0 ? (
              <ArrowDownRight className="h-3 w-3 text-fresh-salmon-dark" />
            ) : (
              <Minus className="h-3 w-3 text-muted-foreground" />
            )}
            <span
              className={cn(
                'tabular-nums',
                delta > 0 ? 'text-bright-moss-dark' : delta < 0 ? 'text-fresh-salmon-dark' : 'text-muted-foreground',
              )}
            >
              {(delta * 100).toFixed(1)}%
            </span>
            {deltaLabel && <span className="text-muted-foreground">{deltaLabel}</span>}
          </div>
        )}
        {hint && !loading && (
          <p className="mt-2 text-xs text-muted-foreground">{hint}</p>
        )}
      </CardContent>
    </Card>
  );
}
